import { Box, Button, Dialog, DialogTitle } from '@mui/material';
import useComponentSize from '@rehooks/component-size';
import * as React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { AllListDialogContentComponentFactory } from '~/components/MyWorkspace/ListWidget';
import { Widget } from '~/components/MyWorkspace/Widget';
import { useVisible } from '~/hooks/useVisible';
import { People } from '~/services/Http';
import { useGetTeamAnniversaries } from '~/services/Http/hooks';

import { WidgetProps, getWidgetDataRequestRoles } from '../../data/widgets';

import { Content } from './components/Content';
import { DialogAnniversary } from './components/DialogAnniversary';
import { useSxStyles } from './useStyles';

export interface Props extends WidgetProps {}

const AllTeamAnniversariesDialogContent = AllListDialogContentComponentFactory({
  ListItemComponent: DialogAnniversary,
  getIdentifier: (item: People.AnniversaryDto) =>
    `${item.employee.id}-${item.type}`,
});

export const TeamAnniversariesWidget: React.FC<Props> = ({
  title,
  width,
  config,
}) => {
  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const dataRequestRoles = useMemo(() => {
    return getWidgetDataRequestRoles(config?.role);
  }, [config]);

  const getTeamAnniversariesReqParams = {
    employeeId: 'me',
    roles: dataRequestRoles,
  };
  const {
    data: teamAnniversaries = [],
    loading,
    failedToLoad,
  } = useGetTeamAnniversaries(getTeamAnniversariesReqParams);

  const {
    isVisible: isAllTeamAnniversariesDialogOpen,
    show: showAllTeamAnniversaries,
    hide: hideAllTeamAnniversariesDialog,
  } = useVisible();

  const filteredTeamAnniversaries = teamAnniversaries.filter(
    (anniversary) => !!anniversary.employee,
  );
  const containerRef = React.useRef<HTMLDivElement>(null);
  const { height } = useComponentSize(containerRef);

  const noOfFittingColumns = Math.floor(width / 118);
  const noOfFittingRows = Math.floor(height / 120);

  return (
    <>
      <Widget
        title={title || t('teamAnniversaries')}
        content={
          <Box ref={containerRef} sx={sxStyles.refDiv}>
            <Content
              teamAnniversariesLoading={loading}
              teamAnniversaries={filteredTeamAnniversaries}
              failedToLoadTeamAnniversaries={failedToLoad}
              containerWidth={width}
              containerHeight={height}
              noOfFittingColumns={noOfFittingColumns}
              noOfFittingRows={noOfFittingRows}
            />
          </Box>
        }
        actions={
          <Button
            size="small"
            color="primary"
            disabled={
              filteredTeamAnniversaries.length <=
              noOfFittingColumns * noOfFittingRows
            }
            onClick={showAllTeamAnniversaries}
            data-cy="show-all"
          >
            {t('showAll')}
          </Button>
        }
        dataCy="team-anniversaries-widget"
      />
      <Dialog
        open={isAllTeamAnniversariesDialogOpen}
        onClose={hideAllTeamAnniversariesDialog}
        data-cy="all-list-widget-dialog"
      >
        <DialogTitle>{t('teamAnniversaries')}</DialogTitle>
        <AllTeamAnniversariesDialogContent
          listItems={filteredTeamAnniversaries}
        />
      </Dialog>
    </>
  );
};
